/**
 * ReadingPathway — the guided checklist for first-time Watch users.
 *
 * Task 4: derives its steps from JOURNEY_CHAPTERS instead of a local
 * hardcoded DEFAULT_STEPS. The displayed chapters are sourced from the
 * public Watch configuration so the pathway cannot reintroduce retired
 * Journey/Predictions destinations.
 *
 * Completion is tracked per chart_id via chapterProgress (localStorage).
 * Each step can be clicked to navigate; a checked step is marked read.
 * Unread steps remain reachable; a number means "not yet read", never
 * "premium locked". Progress is distinct from entitlement gating.
 */
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import { useFaceGate } from "./FaceGateContext";
import {
  JOURNEY_CHAPTERS,
  PATHWAY_CHAPTER_IDS,
  WATCH_CHAPTER_IDS,
  chapterForPathname,
  type JourneyChapter,
  type JourneyChapterId,
  type WatchChapterId,
} from "../../lib/journey";

interface PathwayStep {
  chapter: Exclude<JourneyChapterId, 0>;
  label: string;
  to: string;
}

function buildDefaultSteps(): PathwayStep[] {
  return PATHWAY_CHAPTER_IDS
    .map((id) => {
      const ch = JOURNEY_CHAPTERS.find((c) => c.id === id);
      if (!ch || ch.id === 0) return null;
      return {
        chapter: ch.id as Exclude<JourneyChapterId, 0>,
        label: ch.name,
        to: ch.baseRoute,
      };
    })
    .filter((s): s is PathwayStep => s !== null);
}

interface Props {
  chartId: string | null;
  steps?: PathwayStep[];
}

export function ReadingPathway({ chartId, steps = buildDefaultSteps() }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const gate = useFaceGate();

  if (!chartId) return null;

  // Determine the currently active step from the route prefix.
  const activeChapterId = chapterForPathname(pathname);
  const allComplete = steps.every((s) => gate.isComplete(s.chapter));

  return (
    <div className="reading-pathway panel-raised p-4">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <p className="font-caps text-[9px] tracking-[0.2em] uppercase text-brass-400">
            {t("ui.web.long_tail.watch.reading_pathway")}
          </p>
          <p className="font-body text-sm text-parchment-200 mt-0.5">
            {allComplete
              ? t("ui.web.long_tail.watch.all_chapters_read")
              : t("ui.web.long_tail.watch.progress_help")}
          </p>
        </div>
      </div>

      <ol className="flex flex-wrap gap-2" aria-label={t("ui.web.long_tail.watch.reading_pathway_aria") }>
        {steps.map((s) => {
          const complete = gate.isComplete(s.chapter);
          const isActive = s.chapter === activeChapterId;
          const route = `${s.to}/${chartId}`;
          const ch: JourneyChapter | undefined = JOURNEY_CHAPTERS.find((c) => c.id === s.chapter);
          // Frozen IA: pathway uses the same bezel position as ChapterNav
          // (Chapter N of 6), never an independent per-list index or Roman mark.
          const position = WATCH_CHAPTER_IDS.indexOf(s.chapter as WatchChapterId) + 1;
          return (
            <li key={s.chapter} className="contents">
              <Link
                to={route}
                aria-current={isActive ? "step" : undefined}
                aria-label={ch ? `${t("ui.web.watch.header.eyebrow", { x: position, total: WATCH_CHAPTER_IDS.length })} — ${ch.name}` : undefined}
                className={[
                  "reading-pathway__step",
                  complete ? "is-complete" : "",
                  isActive ? "is-active" : "",
                  !complete && !isActive ? "is-unread" : "",
                ].join(" ")}
              >
                <span className="reading-pathway__mark" aria-hidden="true">
                  {complete ? "✓" : String(position)}
                </span>
                <span className="reading-pathway__label">{s.label}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default ReadingPathway;
